"use client"

import { useEffect, useState } from "react"
import { SlidersHorizontal, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { ChannelIcon } from "@/components/ui/channel-icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { cn } from "@/lib/utils"
import { idOfScopedValue, sourceOfScopedValue } from "@/lib/inbox/scoped-id"
import {
  ATTENTION_HINT,
  INBOX_SOURCE_KINDS,
  INBOX_SOURCE_LABELS,
  type InboxFacet,
  type InboxFilterOptions,
  type InboxSourceCapabilities,
  type InboxSourceKind,
  type UnifiedInboxFilters,
} from "@/lib/inbox/types"

/** Long enough that a typed phone number is one query, not eleven. */
const SEARCH_DEBOUNCE_MS = 400

const ANY = "__any__"

/** A control the surface has already decided — an agent inbox names its agent and its source. */
export type FixedInboxControl = "agent" | "channelType"

interface InboxFilterBarProps {
  filters: UnifiedInboxFilters
  /** Only the sources this operator may read — the rest are not offered at all. */
  capabilities: InboxSourceCapabilities[]
  options: InboxFilterOptions[]
  /** Set when the options failed to load or to refresh; `options` says which. */
  optionsError: Error | null
  onChange: (filters: UnifiedInboxFilters) => void
  /**
   * Controls omitted because the surface owns their value. Cosmetic only: an owned
   * value never lives in `filters`, or `Clear all` would wipe it — the surface merges
   * it at the query instead.
   */
  fixed?: readonly FixedInboxControl[]
}

/**
 * Search plus everything else behind one button. The controls used to stand
 * open and took a third of the list column; what is actually set now reads back
 * as chips, which is the part an operator has to see.
 */
export function InboxFilterBar({
  filters,
  capabilities,
  options,
  optionsError,
  onChange,
  fixed = [],
}: InboxFilterBarProps) {
  const chips = activeChips(filters, options, onChange, fixed)

  return (
    <div className="flex flex-col gap-2 border-b p-3">
      {optionsError && (
        <p className="text-[11px] text-destructive">
          <span>{options.length > 0
            ? "Agent, channel and status choices could not be refreshed, so what is offered below may be out of date: "
            : "Agent, channel and status choices could not be loaded, so they are not offered and the list is not narrowed by channel: "}</span>
          {optionsError.message}
        </p>
      )}

      <div className="flex items-center gap-2">
        <ContactSearch
          value={filters.contact ?? ""}
          onCommit={(contact) => onChange({ ...filters, contact })}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 flex-shrink-0 gap-1.5 px-2.5">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>Filters</span>
              {chips.length > 0 && (
                <Badge className="h-4 min-w-4 px-1 text-[10px]">{chips.length}</Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] space-y-3">
            <FilterControls
              filters={filters}
              capabilities={capabilities}
              options={options}
              onChange={onChange}
              fixed={fixed}
            />
          </PopoverContent>
        </Popover>
      </div>

      {!fixed.includes("channelType") && (
        <ChannelTypeChips filters={filters} options={options} onChange={onChange} />
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {chips.map((chip) => (
            <span
              key={chip.key}
              title={chip.hint}
              className="inline-flex h-6 items-center gap-1 rounded-full border bg-muted/60 pl-2 pr-1 text-[11px]"
            >
              {chip.label}
              <button
                onClick={chip.clear}
                aria-label={`Remove filter: ${chip.label}`}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {chips.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => onChange({})}
            >
              Clear all
            </Button>
          )}
        </div>
      )}

      {/* Stays on screen after the popover closes: these filters hide things on a
          rule the result cannot show, so an empty list has to be able to read as
          «nothing recent is waiting» rather than «this is broken». */}
      {chips
        .filter((chip) => chip.hint)
        .map((chip) => (
          <p key={`${chip.key}-hint`} className="text-[11px] text-muted-foreground">
            {chip.hint}
          </p>
        ))}
    </div>
  )
}

/**
 * The one control that says which sources are asked. Values are source-prefixed, so
 * `Email` and `Calls` sit beside `Telegram` as peers without a second vocabulary —
 * stripping the prefix yields the `ChannelIcon` key.
 */
function ChannelTypeChips({
  filters,
  options,
  onChange,
}: Omit<InboxFilterBarProps, "capabilities" | "optionsError">) {
  const selected = filters.channelTypes ?? []
  const offered = options.flatMap((group) => group.channelTypes)
  // A selection missing from `options` still renders, labelled with its raw type:
  // options are cached for five minutes, and a chip row that silently drops a value
  // would leave the list narrowed by something the operator cannot see or undo.
  const stale = selected
    .filter((id) => !offered.some((type) => type.id === id))
    .map((id) => ({ id, label: idOfScopedValue(id) }))
  const types = [...offered, ...stale]

  if (types.length < 2 && selected.length === 0) return null

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]
    onChange({ ...filters, channelTypes: next.length ? next : undefined })
  }

  // One scrollable row below `split`: three wrapped rows of chips left a phone no
  // room for the list they filter. The bleed keeps the focus ring inside the scroll box.
  return (
    <div className="-mx-3 -my-1 flex items-center gap-1.5 overflow-x-auto px-3 py-1 split:mx-0 split:flex-wrap split:overflow-visible split:px-0">
      <ChannelTypeChip
        label="All"
        pressed={selected.length === 0}
        onClick={() => onChange({ ...filters, channelTypes: undefined })}
      />
      {types.map((type) => (
        <ChannelTypeChip
          key={type.id}
          label={type.label}
          iconKey={idOfScopedValue(type.id)}
          pressed={selected.includes(type.id)}
          onClick={() => toggle(type.id)}
        />
      ))}
    </div>
  )
}

function ChannelTypeChip({
  label,
  iconKey,
  pressed,
  onClick,
}: {
  label: string
  iconKey?: string
  pressed: boolean
  onClick: () => void
}) {
  return (
    <Button
      size="sm"
      variant={pressed ? "default" : "outline"}
      aria-pressed={pressed}
      className="h-7 shrink-0 gap-1.5 px-2 text-xs"
      onClick={onClick}
    >
      {iconKey && (
        <span aria-hidden="true" className="inline-flex shrink-0">
          <ChannelIcon channelType={iconKey} size={14} />
        </span>
      )}
      {label}
    </Button>
  )
}

/** Which sources the chip selection asks for; all of them when nothing is selected. */
function selectedSources(filters: UnifiedInboxFilters): ReadonlySet<InboxSourceKind> {
  const selected = filters.channelTypes ?? []
  if (selected.length === 0) return new Set(INBOX_SOURCE_KINDS)
  return new Set(
    selected
      .map(sourceOfScopedValue)
      .filter((kind): kind is InboxSourceKind => kind !== null),
  )
}

function FilterControls({
  filters,
  capabilities,
  options,
  onChange,
  fixed = [],
}: Omit<InboxFilterBarProps, "optionsError">) {
  // A control that provably does nothing is worse than no control: a State
  // control is offered only while a source on screen declares its facet, and
  // `Conversations only` narrows email and nothing else.
  const shown = selectedSources(filters)
  const declared = (facet: InboxFacet) =>
    capabilities.some((source) => shown.has(source.kind) && source.facets.includes(facet))
  const emailShown = capabilities.some((source) => source.kind === "email") && shown.has("email")
  const stateOffered = declared("attention") || declared("unread") || emailShown

  return (
    <>
      {stateOffered && (
      <FilterGroup label="State">
        <div className="flex flex-wrap items-center gap-1.5">
          {declared("attention") && (
            <Button
              size="sm"
              variant={filters.attentionOnly ? "default" : "outline"}
              className={cn("h-7 px-2 text-xs", filters.attentionOnly && "bg-amber-500 hover:bg-amber-500/90")}
              title={ATTENTION_HINT}
              onClick={() =>
                onChange({ ...filters, attentionOnly: filters.attentionOnly ? undefined : true })
              }
            >
              Needs a human
            </Button>
          )}
          {declared("unread") && (
            <Button
              size="sm"
              variant={filters.unreadOnly ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => onChange({ ...filters, unreadOnly: filters.unreadOnly ? undefined : true })}
            >
              Unread
            </Button>
          )}
          {emailShown && (
            <Button
              size="sm"
              variant={filters.conversationsOnly ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              title="Hides email threads that are a single message nobody ever acted on."
              onClick={() =>
                onChange({ ...filters, conversationsOnly: filters.conversationsOnly ? undefined : true })
              }
            >
              Conversations only
            </Button>
          )}
        </div>
      </FilterGroup>
      )}

      <FilterGroup label="Owner">
        {!fixed.includes("agent") && (
          <GroupedSelect
            placeholder="Any agent"
            value={filters.agent}
            groups={options.map((group) => ({
              source: group.source,
              items: group.agents.map((agent) => ({ id: agent.id, label: agent.name })),
            }))}
            onChange={(agent) => onChange({ ...filters, agent })}
          />
        )}
        <GroupedSelect
          placeholder="Any channel"
          value={filters.channel}
          groups={options.map((group) => ({ source: group.source, items: group.channels }))}
          onChange={(channel) => onChange({ ...filters, channel })}
        />
        <GroupedSelect
          placeholder="Any status"
          value={filters.status}
          groups={options.map((group) => ({ source: group.source, items: group.statuses }))}
          onChange={(status) => onChange({ ...filters, status })}
        />
      </FilterGroup>

      <FilterGroup label="Date range">
        <div className="flex items-center gap-2">
          <Input
            type="date"
            className="h-7 text-xs"
            value={filters.dateFrom?.slice(0, 10) ?? ""}
            onChange={(event) => onChange({ ...filters, dateFrom: toStartOfDayIso(event.target.value) })}
            aria-label="From date"
          />
          <Input
            type="date"
            className="h-7 text-xs"
            value={filters.dateTo?.slice(0, 10) ?? ""}
            onChange={(event) => onChange({ ...filters, dateTo: toEndOfDayIso(event.target.value) })}
            aria-label="To date"
          />
        </div>
      </FilterGroup>
    </>
  )
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      {children}
    </div>
  )
}

interface FilterChip {
  key: string
  label: string
  /** Why an empty list can be the right answer — see the hint row below the chips. */
  hint?: string
  clear: () => void
}

/** A chip names the value, not the key: `Instagram @acme`, never `channel`. */
function activeChips(
  filters: UnifiedInboxFilters,
  options: InboxFilterOptions[],
  onChange: (filters: UnifiedInboxFilters) => void,
  fixed: readonly FixedInboxControl[],
): FilterChip[] {
  const chips: FilterChip[] = []

  if (filters.contact) {
    chips.push({
      key: "contact",
      label: `“${filters.contact}”`,
      clear: () => onChange({ ...filters, contact: undefined }),
    })
  }
  if (filters.attentionOnly) {
    chips.push({
      key: "attention",
      label: "Needs a human",
      hint: ATTENTION_HINT,
      clear: () => onChange({ ...filters, attentionOnly: undefined }),
    })
  }
  if (filters.unreadOnly) {
    chips.push({
      key: "unread",
      label: "Unread",
      clear: () => onChange({ ...filters, unreadOnly: undefined }),
    })
  }
  if (filters.conversationsOnly) {
    chips.push({
      key: "conversations",
      label: "Conversations only",
      hint: "Email threads with a single untouched message are hidden.",
      clear: () => onChange({ ...filters, conversationsOnly: undefined }),
    })
  }
  if (filters.agent && !fixed.includes("agent")) {
    chips.push({
      key: "agent",
      label: optionLabel(options, "agents", filters.agent),
      clear: () => onChange({ ...filters, agent: undefined }),
    })
  }
  if (filters.channel) {
    chips.push({
      key: "channel",
      label: optionLabel(options, "channels", filters.channel),
      clear: () => onChange({ ...filters, channel: undefined }),
    })
  }
  if (filters.status) {
    chips.push({
      key: "status",
      label: optionLabel(options, "statuses", filters.status),
      clear: () => onChange({ ...filters, status: undefined }),
    })
  }
  if (filters.dateFrom) {
    chips.push({
      key: "date-from",
      label: `From ${filters.dateFrom.slice(0, 10)}`,
      clear: () => onChange({ ...filters, dateFrom: undefined }),
    })
  }
  if (filters.dateTo) {
    chips.push({
      key: "date-to",
      label: `To ${filters.dateTo.slice(0, 10)}`,
      clear: () => onChange({ ...filters, dateTo: undefined }),
    })
  }
  return chips
}

/** Falls back to the raw id: a stale selection must still be visible and removable. */
function optionLabel(
  options: InboxFilterOptions[],
  facet: "agents" | "channels" | "statuses",
  id: string,
): string {
  for (const group of options) {
    const found =
      facet === "agents"
        ? group.agents.find((agent) => agent.id === id)
        : group[facet].find((item) => item.id === id)
    if (found) return "name" in found ? found.name : found.label
  }
  return id
}

/**
 * Local while typing, committed on a pause. Every keystroke is otherwise a
 * three-source query, and the phone search runs an exact match that only becomes
 * meaningful once the whole number is there.
 */
function ContactSearch({
  value,
  onCommit,
}: {
  value: string
  onCommit: (term: string | undefined) => void
}) {
  const [term, setTerm] = useState(value)
  const debounced = useDebouncedValue(term, SEARCH_DEBOUNCE_MS)

  // Follows the prop: clearing the chip has to empty the box, and a debounce in
  // flight at that moment must not commit the old term straight back.
  useEffect(() => {
    setTerm(value)
  }, [value])

  useEffect(() => {
    const next = debounced.trim()
    if (next === value.trim()) return
    onCommit(next.length > 0 ? next : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- committing on the debounced term only
  }, [debounced])

  return (
    <Input
      value={term}
      onChange={(event) => setTerm(event.target.value)}
      placeholder="Name, @handle, email address, phone…"
      className="h-8 text-xs"
      aria-label="Search conversations by contact"
    />
  )
}

function GroupedSelect({
  placeholder,
  value,
  groups,
  onChange,
}: {
  placeholder: string
  value: string | undefined
  groups: Array<{ source: InboxSourceKind; items: Array<{ id: string; label: string }> }>
  onChange: (value: string | undefined) => void
}) {
  const populated = groups.filter((group) => group.items.length > 0)
  if (populated.length === 0) return null

  return (
    <Select value={value ?? ANY} onValueChange={(next) => onChange(next === ANY ? undefined : next)}>
      <SelectTrigger className="h-7 w-full text-xs">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{placeholder}</SelectItem>
        {/* Grouped by source so "Instagram @acme" and "support@acme.com" can sit
            in one control without pretending to be the same kind of thing. */}
        {populated.map((group) => (
          <SelectGroup key={group.source}>
            <SelectLabel>{INBOX_SOURCE_LABELS[group.source].many}</SelectLabel>
            {group.items.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  )
}

function toStartOfDayIso(value: string): string | undefined {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined
}

function toEndOfDayIso(value: string): string | undefined {
  return value ? new Date(`${value}T23:59:59.999Z`).toISOString() : undefined
}
